"use client";

import { Button } from "@west-tennessee-consulting/ui";
import { Wand2, Maximize2, Minimize2, Loader2 } from "lucide-react";
import { useMspDocAi, type MspDocAiAssistMode } from "../../hooks/useMspDocAi";

interface AiAssistToolbarProps {
  content: string;
  onResult: (content: string) => void;
}

const ACTIONS: Array<{ mode: MspDocAiAssistMode; label: string; icon: typeof Wand2 }> = [
  { mode: "improve", label: "Improve", icon: Wand2 },
  { mode: "expand", label: "Expand", icon: Maximize2 },
  { mode: "summarize", label: "Summarize", icon: Minimize2 },
];

/**
 * Inline content-assist actions that transform the current markdown in place.
 * Disabled until there is content to act on.
 */
export function AiAssistToolbar({ content, onResult }: AiAssistToolbarProps) {
  const { assist, isGenerating, error } = useMspDocAi();
  const hasContent = content.trim().length > 0;

  const handle = async (mode: MspDocAiAssistMode) => {
    const result = await assist({ mode, content });
    if (result) onResult(result);
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">AI:</span>
        {ACTIONS.map(({ mode, label, icon: Icon }) => (
          <Button
            key={mode}
            type="button"
            variant="outline"
            size="sm"
            disabled={!hasContent || isGenerating}
            onClick={() => handle(mode)}
          >
            {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
            <span className="ml-1">{label}</span>
          </Button>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
